import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import {
    Users,
    DollarSign,
    TrendingUp,
    Link as LinkIcon,
    Download,
    Plus,
    Edit,
    Trash2,
    Check,
    X,
    Copy,
    Loader2,
    Star,
    Instagram,
    Globe,
    Calendar,
    MoreVertical,
    Eye,
    Search,
    Filter,
    Ban,
    CheckCircle,
    Clock,
    ArrowUpRight,
    Percent,
    Hash,
    CreditCard,
    AlertTriangle,
} from 'lucide-react';
import { formatCurrency } from '@shared/utils';

// ================== ADMIN DASHBOARD ==================
export function AdminDashboardPage() {
    const { data: stats } = useQuery({
        queryKey: ['admin-stats'],
        queryFn: async () => {
            const [tenantsResult, athletesResult, invoicesResult, influencersResult] = await Promise.all([
                supabase
                    .from('tenants')
                    .select('id', { count: 'exact' }),
                supabase
                    .from('student_pairings')
                    .select('id', { count: 'exact' })
                    .eq('status', 'active'),
                supabase
                    .from('invoices')
                    .select('amount_cents')
                    .eq('status', 'paid')
                    .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()),
                supabase
                    .from('influencer_profiles')
                    .select('id', { count: 'exact' })
                    .eq('status', 'active'),
            ]);

            const monthlyRevenue = (invoicesResult.data?.reduce((acc, inv) => acc + (inv.amount_cents || 0), 0) || 0) / 100;

            return {
                tenants: tenantsResult.count || 0,
                athletes: athletesResult.count || 0,
                mrr: monthlyRevenue,
                influencers: influencersResult.count || 0,
            };
        },
    });

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Admin Dashboard
                    </h1>
                    <p className="text-gray-500"> Visão geral da plataforma </p>
                </div>
                <button className="btn-secondary">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar Relatório
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500"> Personals (Tenants) </p>
                            <p className="text-2xl font-bold"> {stats?.tenants || 0} </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500"> Atletas Ativos </p>
                            <p className="text-2xl font-bold"> {stats?.athletes || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500"> Receita (30 dias) </p>
                            <p className="text-2xl font-bold">
                                {formatCurrency(stats?.mrr || 0)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                            <Star className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500"> Influencers </p>
                            <p className="text-2xl font-bold"> {stats?.influencers || 0} </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-lg font-semibold mb-4"> Atividade Recente </h2>
                <p className="text-gray-500"> Dashboard conectado com dados reais da plataforma.</p>
            </div>
        </div>
    );
}

// ================== INFLUENCERS PAGE ==================
interface Influencer {
    id: string;
    user_id: string;
    display_name: string;
    bio: string | null;
    instagram_handle: string | null;
    youtube_channel: string | null;
    total_referrals: number;
    total_earnings_cents: number;
    commission_rate: number;
    status: 'pending' | 'active' | 'suspended';
    created_at: string;
}

export function InfluencersPage() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [showModal, setShowModal] = useState(false);
    const [editingInfluencer, setEditingInfluencer] = useState<Influencer | null>(null);

    const { data: influencers, isLoading } = useQuery({
        queryKey: ['admin-influencers'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('influencer_profiles')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data as Influencer[];
        },
    });

    const updateStatusMutation = useMutation({
        mutationFn: async ({ id, status }: { id: string; status: string }) => {
            const { error } = await supabase
                .from('influencer_profiles')
                .update({ status })
                .eq('id', id);
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-influencers'] }),
    });

    const updateCommissionMutation = useMutation({
        mutationFn: async ({ id, commission_rate }: { id: string; commission_rate: number }) => {
            const { error } = await supabase
                .from('influencer_profiles')
                .update({ commission_rate })
                .eq('id', id);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-influencers'] });
            setShowModal(false);
            setEditingInfluencer(null);
        },
    });

    const filtered = influencers?.filter(inf => {
        const matchesSearch = inf.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inf.instagram_handle?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || inf.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Ativo</span>;
            case 'pending':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pendente</span>;
            case 'suspended':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Suspenso</span>;
            default:
                return null;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Star className="w-6 h-6" />
                        Influencers
                    </h1>
                    <p className="text-gray-500">{influencers?.length || 0} influencers cadastrados</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou Instagram..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input pl-10"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="input w-40"
                >
                    <option value="all">Todos</option>
                    <option value="active">Ativos</option>
                    <option value="pending">Pendentes</option>
                    <option value="suspended">Suspensos</option>
                </select>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : filtered && filtered.length > 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Influencer</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referências</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ganhos</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comissão</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                            {filtered.map((inf) => (
                                <tr key={inf.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="px-4 py-4">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">{inf.display_name}</p>
                                            {inf.instagram_handle && (
                                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                                    <Instagram className="w-3 h-3" />
                                                    @{inf.instagram_handle}
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">{getStatusBadge(inf.status)}</td>
                                    <td className="px-4 py-4">
                                        <span className="font-medium">{inf.total_referrals}</span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className="font-medium text-green-600">{formatCurrency(inf.total_earnings_cents / 100)}</span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className="font-medium">{inf.commission_rate}%</span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingInfluencer(inf);
                                                    setShowModal(true);
                                                }}
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                                title="Editar"
                                            >
                                                <Edit className="w-4 h-4 text-gray-500" />
                                            </button>
                                            {inf.status === 'pending' && (
                                                <button
                                                    onClick={() => updateStatusMutation.mutate({ id: inf.id, status: 'active' })}
                                                    className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                                                    title="Aprovar"
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                </button>
                                            )}
                                            {inf.status === 'active' && (
                                                <button
                                                    onClick={() => updateStatusMutation.mutate({ id: inf.id, status: 'suspended' })}
                                                    className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                                                    title="Suspender"
                                                >
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                            )}
                                            {inf.status === 'suspended' && (
                                                <button
                                                    onClick={() => updateStatusMutation.mutate({ id: inf.id, status: 'active' })}
                                                    className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                                                    title="Reativar"
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-16">
                    <Star className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Nenhum influencer encontrado</h3>
                    <p className="text-gray-500">Ajuste os filtros ou aguarde novas inscrições.</p>
                </div>
            )}

            {/* Edit Modal */}
            {showModal && editingInfluencer && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-bold mb-4">Editar Comissão</h3>
                        <p className="text-gray-500 mb-4">Influencer: {editingInfluencer.display_name}</p>
                        <div className="mb-4">
                            <label className="label">Taxa de Comissão (%)</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={editingInfluencer.commission_rate}
                                onChange={(e) => setEditingInfluencer({ ...editingInfluencer, commission_rate: parseFloat(e.target.value) })}
                                className="input"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                                Cancelar
                            </button>
                            <button
                                onClick={() => updateCommissionMutation.mutate({ id: editingInfluencer.id, commission_rate: editingInfluencer.commission_rate })}
                                className="btn-primary flex-1"
                            >
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ================== AFFILIATE CODES PAGE ==================
interface AffiliateCode {
    id: string;
    influencer_id: string;
    code: string;
    discount_percent: number;
    uses_count: number;
    max_uses: number | null;
    expires_at: string | null;
    is_active: boolean;
    created_at: string;
    influencer?: {
        display_name: string;
    };
}

export function AffiliateCodesPage() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newCode, setNewCode] = useState({ code: '', discount_percent: 10, influencer_id: '' });

    const { data: influencers } = useQuery({
        queryKey: ['influencers-select'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('influencer_profiles')
                .select('id, display_name')
                .eq('status', 'active');
            if (error) throw error;
            return data;
        },
    });

    const { data: codes, isLoading } = useQuery({
        queryKey: ['admin-affiliate-codes'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('affiliate_codes')
                .select(`
                    *,
                    influencer:influencer_id (
                        display_name
                    )
                `)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data as AffiliateCode[];
        },
    });

    const createCodeMutation = useMutation({
        mutationFn: async (codeData: typeof newCode) => {
            const { error } = await supabase
                .from('affiliate_codes')
                .insert({
                    code: codeData.code.toUpperCase(),
                    discount_percent: codeData.discount_percent,
                    influencer_id: codeData.influencer_id,
                    is_active: true,
                });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-affiliate-codes'] });
            setShowCreateModal(false);
            setNewCode({ code: '', discount_percent: 10, influencer_id: '' });
        },
    });

    const toggleCodeMutation = useMutation({
        mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
            const { error } = await supabase
                .from('affiliate_codes')
                .update({ is_active })
                .eq('id', id);
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-affiliate-codes'] }),
    });

    const deleteCodeMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('affiliate_codes')
                .delete()
                .eq('id', id);
            if (error) throw error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-affiliate-codes'] }),
    });

    const filteredCodes = codes?.filter(c =>
        c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.influencer?.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const copyCode = (code: string) => {
        navigator.clipboard.writeText(code);
    };

    const generateRandomCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let result = 'FIT';
        for (let i = 0; i < 5; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setNewCode({ ...newCode, code: result });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Hash className="w-6 h-6" />
                        Códigos de Afiliados
                    </h1>
                    <p className="text-gray-500">{codes?.length || 0} códigos cadastrados</p>
                </div>
                <button onClick={() => setShowCreateModal(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Código
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10"
                />
            </div>

            {/* Codes Grid */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : filteredCodes && filteredCodes.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredCodes.map((code) => (
                        <div key={code.id} className={`card ${!code.is_active ? 'opacity-50' : ''}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl font-bold text-primary-600">{code.code}</span>
                                        <button onClick={() => copyCode(code.code)} className="p-1 hover:bg-gray-100 rounded">
                                            <Copy className="w-4 h-4 text-gray-400" />
                                        </button>
                                    </div>
                                    <p className="text-sm text-gray-500">{code.influencer?.display_name || 'Sem influencer'}</p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${code.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                    {code.is_active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                                <div className="flex items-center gap-1">
                                    <Percent className="w-4 h-4" />
                                    {code.discount_percent}% off
                                </div>
                                <div className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {code.uses_count} usos
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => toggleCodeMutation.mutate({ id: code.id, is_active: !code.is_active })}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${code.is_active
                                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                        }`}
                                >
                                    {code.is_active ? 'Desativar' : 'Ativar'}
                                </button>
                                <button
                                    onClick={() => {
                                        if (confirm('Deseja excluir este código?')) {
                                            deleteCodeMutation.mutate(code.id);
                                        }
                                    }}
                                    className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-16">
                    <Hash className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Nenhum código encontrado</h3>
                    <p className="text-gray-500">Crie um novo código para começar.</p>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-bold mb-4">Novo Código de Afiliado</h3>

                        <div className="space-y-4">
                            <div>
                                <label className="label">Código</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCode.code}
                                        onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                                        placeholder="FITCODE10"
                                        className="input flex-1"
                                    />
                                    <button onClick={generateRandomCode} className="btn-secondary">
                                        Gerar
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="label">Desconto (%)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={newCode.discount_percent}
                                    onChange={(e) => setNewCode({ ...newCode, discount_percent: parseInt(e.target.value) })}
                                    className="input"
                                />
                            </div>

                            <div>
                                <label className="label">Influencer</label>
                                <select
                                    value={newCode.influencer_id}
                                    onChange={(e) => setNewCode({ ...newCode, influencer_id: e.target.value })}
                                    className="input"
                                >
                                    <option value="">Selecione...</option>
                                    {influencers?.map((inf) => (
                                        <option key={inf.id} value={inf.id}>{inf.display_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setShowCreateModal(false)} className="btn-secondary flex-1">
                                Cancelar
                            </button>
                            <button
                                onClick={() => createCodeMutation.mutate(newCode)}
                                disabled={!newCode.code || !newCode.influencer_id}
                                className="btn-primary flex-1"
                            >
                                Criar Código
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ================== COMMISSIONS PAGE ==================
interface Commission {
    id: string;
    influencer_id: string;
    amount_cents: number;
    status: 'pending' | 'paid' | 'cancelled';
    paid_at: string | null;
    created_at: string;
    influencer?: {
        display_name: string;
    };
}

export function CommissionsPage() {
    const queryClient = useQueryClient();
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const { data: commissions, isLoading } = useQuery({
        queryKey: ['admin-commissions'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('commissions')
                .select(`
                    *,
                    influencer:influencer_id (
                        display_name
                    )
                `)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data as Commission[];
        },
    });

    const { data: stats } = useQuery({
        queryKey: ['commission-stats'],
        queryFn: async () => {
            const { data: pending } = await supabase
                .from('commissions')
                .select('amount_cents')
                .eq('status', 'pending');

            const { data: paid } = await supabase
                .from('commissions')
                .select('amount_cents')
                .eq('status', 'paid');

            return {
                pending: (pending?.reduce((acc, c) => acc + c.amount_cents, 0) || 0) / 100,
                paid: (paid?.reduce((acc, c) => acc + c.amount_cents, 0) || 0) / 100,
            };
        },
    });

    const payCommissionMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('commissions')
                .update({ status: 'paid', paid_at: new Date().toISOString() })
                .eq('id', id);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-commissions'] });
            queryClient.invalidateQueries({ queryKey: ['commission-stats'] });
        },
    });

    const payAllPendingMutation = useMutation({
        mutationFn: async () => {
            const { error } = await supabase
                .from('commissions')
                .update({ status: 'paid', paid_at: new Date().toISOString() })
                .eq('status', 'pending');
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-commissions'] });
            queryClient.invalidateQueries({ queryKey: ['commission-stats'] });
        },
    });

    const filtered = commissions?.filter(c =>
        statusFilter === 'all' || c.status === statusFilter
    );

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pendente</span>;
            case 'paid':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Pago</span>;
            case 'cancelled':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Cancelado</span>;
            default:
                return null;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <CreditCard className="w-6 h-6" />
                        Comissões
                    </h1>
                    <p className="text-gray-500">{commissions?.length || 0} comissões registradas</p>
                </div>
                <button
                    onClick={() => payAllPendingMutation.mutate()}
                    disabled={!stats?.pending || payAllPendingMutation.isPending}
                    className="btn-primary"
                >
                    {payAllPendingMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Pagar Todas Pendentes ({formatCurrency(stats?.pending || 0)})
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-800">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                            <p className="text-sm text-yellow-700">Pendente</p>
                            <p className="text-2xl font-bold text-yellow-800">{formatCurrency(stats?.pending || 0)}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-sm text-green-700">Total Pago</p>
                            <p className="text-2xl font-bold text-green-800">{formatCurrency(stats?.paid || 0)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter */}
            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input w-48"
            >
                <option value="all">Todas</option>
                <option value="pending">Pendentes</option>
                <option value="paid">Pagas</option>
                <option value="cancelled">Canceladas</option>
            </select>

            {/* Table */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : filtered && filtered.length > 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Influencer</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                            {filtered.map((comm) => (
                                <tr key={comm.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="px-4 py-4 font-medium">{comm.influencer?.display_name || 'N/A'}</td>
                                    <td className="px-4 py-4">
                                        <span className="font-bold text-green-600">{formatCurrency(comm.amount_cents / 100)}</span>
                                    </td>
                                    <td className="px-4 py-4">{getStatusBadge(comm.status)}</td>
                                    <td className="px-4 py-4 text-gray-500">
                                        {new Date(comm.created_at).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                        {comm.status === 'pending' && (
                                            <button
                                                onClick={() => payCommissionMutation.mutate(comm.id)}
                                                className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200"
                                            >
                                                Marcar Pago
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-16">
                    <CreditCard className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Nenhuma comissão encontrada</h3>
                    <p className="text-gray-500">As comissões aparecerão conforme as vendas ocorrerem.</p>
                </div>
            )}
        </div>
    );
}

// ================== USERS PAGE ==================
interface User {
    id: string;
    email: string;
    full_name: string | null;
    role: string;
    created_at: string;
    tenant?: any;
}

export function UsersPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');

    const { data: users, isLoading } = useQuery({
        queryKey: ['admin-users'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('users')
                .select(`
                    id,
                    email,
                    full_name,
                    role,
                    created_at,
                    tenant:tenant_id (
                        name
                    )
                `)
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) throw error;
            return data as User[];
        },
    });

    const filtered = users?.filter(u => {
        const matchesSearch = u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            u.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === 'all' || u.role === roleFilter;
        return matchesSearch && matchesRole;
    });

    const getRoleBadge = (role: string) => {
        switch (role) {
            case 'admin':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Admin</span>;
            case 'personal':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Personal</span>;
            case 'student':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Atleta</span>;
            case 'influencer':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Influencer</span>;
            default:
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{role}</span>;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    Usuários
                </h1>
                <p className="text-gray-500">{users?.length || 0} usuários cadastrados</p>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input pl-10"
                    />
                </div>
                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="input w-40"
                >
                    <option value="all">Todos</option>
                    <option value="admin">Admins</option>
                    <option value="personal">Personals</option>
                    <option value="student">Atletas</option>
                    <option value="influencer">Influencers</option>
                </select>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : filtered && filtered.length > 0 ? (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cadastro</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                            {filtered.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="px-4 py-4">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">{user.full_name || 'Sem nome'}</p>
                                            <p className="text-sm text-gray-500">{user.email}</p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">{getRoleBadge(user.role)}</td>
                                    <td className="px-4 py-4 text-gray-500">{user.tenant?.name || '-'}</td>
                                    <td className="px-4 py-4 text-gray-500">
                                        {new Date(user.created_at).toLocaleDateString('pt-BR')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-16">
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Nenhum usuário encontrado</h3>
                    <p className="text-gray-500">Ajuste os filtros para ver mais resultados.</p>
                </div>
            )}
        </div>
    );
}
